import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"

interface DuplicateReportListDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sourceName: string
  isPending?: boolean
  onConfirm: (newName: string) => void
}

function DuplicateForm({
  sourceName,
  isPending,
  onConfirm,
  onCancel,
}: Readonly<{
  sourceName: string
  isPending?: boolean
  onConfirm: (newName: string) => void
  onCancel: () => void
}>) {
  const { t } = useTranslation()
  const [name, setName] = useState(`${sourceName} (Copy)`)

  const handleConfirm = () => {
    if (name.trim() && !isPending) {
      onConfirm(name.trim())
    }
  }

  return (
    <>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="copy-report-list-name">{t("dialogs.duplicateList.newNameLabel")}</Label>
          <Input
            id="copy-report-list-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleConfirm()
              if (e.key === "Escape" && !isPending) onCancel()
            }}
            placeholder={t("dialogs.createList.namePlaceholder")}
            disabled={isPending}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={isPending}>
          {t("actions.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!name.trim() || isPending}
        >
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("common.loading")}
            </>
          ) : (
            t("actions.save")
          )}
        </Button>
      </DialogFooter>
    </>
  )
}

export function DuplicateReportListDialog({
  open,
  onOpenChange,
  sourceName,
  isPending,
  onConfirm,
}:  Readonly<DuplicateReportListDialogProps>) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={isPending ? undefined : onOpenChange}>
      <DialogContent
        className="sm:max-w-[500px]"
        onInteractOutside={isPending ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle>{t("dialogs.duplicateList.title")}</DialogTitle>
        </DialogHeader>
        {open && (
          <DuplicateForm
            sourceName={sourceName}
            isPending={isPending}
            onConfirm={(name) => {
              onConfirm(name)
            }}
            onCancel={() => {
              onOpenChange(false)
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
